import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { 
    FaStar, 
    FaStarHalfAlt, 
    FaRegStar,
    FaQuoteLeft,
    FaUser,
    FaCalendarAlt,
    FaTimes,
    FaChevronRight,
    FaAward,
    FaThumbsUp,
    FaComment,
    FaSpinner,
    FaExclamationTriangle
} from 'react-icons/fa';
import { 
    COLORS, 
    PanelContent, 
    PanelTitle,
    StatGrid,
    StatCard,
} from './styles';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";

// Styled components
const ReviewsContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 4px;
    
    &::-webkit-scrollbar {
        width: 4px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 2px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(100, 150, 200, 0.3);
        border-radius: 2px;
    }
`;

const ReviewCard = styled.div`
    background: rgba(25, 30, 45, 0.6);
    border: 1px solid ${COLORS.border};
    border-left: 3px solid ${props => {
        const g = AVATAR_GRADIENTS[(props.$colorIndex || 0) % AVATAR_GRADIENTS.length];
        return g.from;
    }};
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
        background: rgba(99, 102, 241, 0.12);
        border-color: ${COLORS.borderAccent};
        border-left-width: 3px;
        transform: translateX(3px);
    }
    
    &:last-child {
        margin-bottom: 0;
    }
    
    @media screen and (max-width: 1920px) {
        padding: 10px;
        margin-bottom: 6px;
    }
`;

const ReviewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
`;

const ReviewerInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const AVATAR_GRADIENTS = [
    { from: '#6366f1', to: '#8b5cf6' },
    { from: '#06b6d4', to: '#3b82f6' },
    { from: '#f59e0b', to: '#ef4444' },
    { from: '#10b981', to: '#14b8a6' },
    { from: '#ec4899', to: '#8b5cf6' },
    { from: '#f97316', to: '#eab308' },
    { from: '#6366f1', to: '#06b6d4' },
    { from: '#14b8a6', to: '#22c55e' },
    { from: '#e11d48', to: '#f43f5e' },
    { from: '#7c3aed', to: '#2563eb' },
];

const Avatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: ${props => {
        const g = AVATAR_GRADIENTS[(props.$colorIndex || 0) % AVATAR_GRADIENTS.length];
        return `linear-gradient(135deg, ${g.from} 0%, ${g.to} 100%)`;
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
    
    @media screen and (max-width: 1920px) {
        width: 28px;
        height: 28px;
        font-size: 10px;
    }
`;

const ReviewerDetails = styled.div`
    display: flex;
    flex-direction: column;
`;

const ReviewerName = styled.span`
    color: ${COLORS.light};
    font-weight: 600;
    font-size: 12px;
    
    @media screen and (max-width: 1920px) {
        font-size: 11px;
    }
`;

const ReviewDate = styled.span`
    color: ${COLORS.muted};
    font-size: 10px;
    display: flex;
    align-items: center;
    gap: 4px;
    
    @media screen and (max-width: 1920px) {
        font-size: 9px;
    }
`;

const StarsContainer = styled.div`
    display: flex;
    gap: 2px;
    color: #fbbf24;
    font-size: 12px;
    
    @media screen and (max-width: 1920px) {
        font-size: 10px;
    }
`;

const ReviewText = styled.p`
    color: ${COLORS.lightSecondary};
    font-size: 11px;
    line-height: 1.5;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    
    @media screen and (max-width: 1920px) {
        font-size: 10px;
        -webkit-line-clamp: 2;
    }
`;

const ReviewFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid ${COLORS.border};
`;

const ServiceType = styled.span`
    background: rgba(99, 102, 241, 0.15);
    border: 1px solid rgba(99, 102, 241, 0.25);
    color: ${COLORS.primaryLight};
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 9px;
    font-weight: 500;
`;

const ReadMore = styled.span`
    color: ${COLORS.primary};
    font-size: 10px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
    
    svg {
        font-size: 8px;
    }
`;

// Modal for full review
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
    background: ${COLORS.bgSecondary};
    border: 1px solid ${COLORS.border};
    border-radius: 16px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: ${COLORS.muted};
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
        background: rgba(239, 68, 68, 0.2);
        color: ${COLORS.danger};
    }
`;

const ModalTitle = styled.h3`
    color: ${COLORS.light};
    font-size: 18px;
    margin: 0 0 16px 0;
    display: flex;
    align-items: center;
    gap: 10px;
    
    svg {
        color: ${COLORS.primary};
    }
`;

const FullReviewText = styled.div`
    color: ${COLORS.lightSecondary};
    font-size: 14px;
    line-height: 1.7;
    margin: 16px 0;
    padding: 16px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
    border-left: 3px solid ${COLORS.primary};
    white-space: pre-wrap;
    
    svg {
        color: ${COLORS.primary};
        opacity: 0.5;
        margin-bottom: 8px;
    }
`;

const RatingBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.15) 100%);
    border: 1px solid rgba(251, 191, 36, 0.3);
    padding: 8px 16px;
    border-radius: 20px;
    color: #fbbf24;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
`;

const OverallRating = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
`;

const BigRating = styled.div`
    font-size: 28px;
    font-weight: 700;
    color: #fbbf24;
    
    @media screen and (max-width: 1920px) {
        font-size: 24px;
    }
`;

const RatingText = styled.div`
    display: flex;
    flex-direction: column;
`;

const RatingCount = styled.span`
    color: ${COLORS.muted};
    font-size: 11px;
    
    @media screen and (max-width: 1920px) {
        font-size: 10px;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${COLORS.muted};
    text-align: center;
    padding: 20px;
    
    svg {
        font-size: 32px;
        margin-bottom: 12px;
        opacity: 0.5;
    }
    
    p {
        font-size: 12px;
        margin: 0;
    }
`;

const LoadingState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${COLORS.muted};
    
    svg {
        margin-right: 8px;
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

// Star rating component
const StarRating = ({ rating }) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.25 && rating % 1 < 0.75;
    const roundUp = rating % 1 >= 0.75;
    
    for (let i = 0; i < fullStars + (roundUp ? 1 : 0); i++) {
        stars.push(<FaStar key={`full-${i}`} />);
    }
    
    if (hasHalfStar) {
        stars.push(<FaStarHalfAlt key="half" />);
    }
    
    const emptyStars = 5 - Math.ceil(rating + (hasHalfStar ? 0 : 0));
    for (let i = 0; i < emptyStars; i++) {
        stars.push(<FaRegStar key={`empty-${i}`} />);
    }
    
    // Ensure we always have exactly 5 stars
    while (stars.length < 5) {
        stars.push(<FaRegStar key={`empty-extra-${stars.length}`} />);
    }
    while (stars.length > 5) {
        stars.pop();
    }
    
    return <StarsContainer>{stars}</StarsContainer>;
};

/**
 * Strip any HTML-like tags from a string (e.g. "/strong" leaking through)
 */
const sanitizeText = (text) => {
    if (!text) return '';
    return text.replace(/<\/?[^>]+(>|$)/g, '').replace(/^\/\w+$/, '').trim();
};

/**
 * Simple deterministic hash for picking a color index from a string
 */
const colorIndexFromId = (id) => {
    if (!id) return 0;
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = ((hash << 5) - hash) + id.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

/** "3 months ago" from a timestamp — matches how the widget dates reviews. */
const toRelativeDate = (value) => {
    if (!value) return '';
    const then = new Date(value);
    if (Number.isNaN(then.getTime())) return '';
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (days < 1) return 'today';
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    if (days < 31) {
        const w = Math.floor(days / 7);
        return `${w} week${w === 1 ? '' : 's'} ago`;
    }
    if (days < 365) {
        const m = Math.floor(days / 30);
        return `${m} month${m === 1 ? '' : 's'} ago`;
    }
    const y = Math.floor(days / 365);
    return `${y} year${y === 1 ? '' : 's'} ago`;
};

const toInitials = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * A stored review -> the shape this panel renders. Where a review came from is
 * shown on the card, so an imported Google review is never mistaken for one
 * collected through the platform.
 */
const mapReview = (r) => {
    const text = r.text || '';
    return {
        id: r._id || r.id,
        name: r.authorDisplay || r.authorName || 'Anonymous',
        initials: toInitials(r.authorDisplay || r.authorName),
        rating: Number(r.rating) || 0,
        dateRelative: toRelativeDate(r.publishedAt || r.submittedAt),
        shortText: text.length > 150 ? `${text.slice(0, 147)}...` : text,
        fullText: text,
        service: r.verified ? 'Verified client' : (r.source === 'imported' ? 'via Google' : 'Client review'),
        adviserReply: r.adviserReply || null,
        language: r.language,
        translatedFrom: r.translatedFrom || null,
        textTruncated: !!r.textTruncated,
        status: r.status
    };
};

/**
 * ExpertReviews Component
 *
 * Shows the adviser's real client reviews — the same rows the public widget
 * serves, read from /reviews/mine.
 */
const ExpertReviews = () => {
    const authenticatedAxios = useAuthenticatedAxios();
    const [selectedReview, setSelectedReview] = useState(null);
    const [stats, setStats] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch the adviser's real reviews.
    //
    // This used to read feedback/stats + feedback/reviews, which derived "reviews"
    // from the internal product survey: no author (every card said "Widget User")
    // and no text, so the body was a canned template picked by hashing the
    // feedback id. Nothing on screen was written by a client. It now reads
    // /reviews/mine — the same rows the public widget shows.
    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                setError(null);
                const { data } = await authenticatedAxios.get('reviews/mine?limit=10');
                if (cancelled) return;

                setStats(data.summary || null);
                setReviews((data.reviews || []).map(mapReview));
            } catch (err) {
                if (cancelled) return;
                console.error('Error fetching reviews:', err);
                setError('Failed to load reviews');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        // Refresh every 5 minutes
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [authenticatedAxios]);

    // Loading state
    if (loading) {
        return (
            <PanelContent>
                <PanelTitle><FaStar style={{ color: '#fbbf24' }} /> Client Reviews</PanelTitle>
                <LoadingState>
                    <FaSpinner />
                    Loading reviews...
                </LoadingState>
            </PanelContent>
        );
    }

    // Error state
    if (error) {
        return (
            <PanelContent>
                <PanelTitle><FaStar style={{ color: '#fbbf24' }} /> Client Reviews</PanelTitle>
                <EmptyState>
                    <FaExclamationTriangle style={{ color: COLORS.warning }} />
                    <p>{error}</p>
                </EmptyState>
            </PanelContent>
        );
    }

    // Straight from the review summary — the same numbers the widget badge shows.
    const averageRating = stats?.average || 0;
    const totalReviews = stats?.total || 0;
    const fiveStarCount = stats?.distribution?.['5'] || 0;
    // Share of reviews the adviser has publicly answered. Previously this tile
    // was hardcoded to 100%, which told the adviser nothing and was never true.
    const replyRate = stats?.replyRate || 0;
    
    return (
        <PanelContent>
            <PanelTitle><FaStar style={{ color: '#fbbf24' }} /> Client Reviews</PanelTitle>
            
            {/* Stats row */}
            <StatGrid $cols={3}>
                <StatCard style={{ textAlign: 'center' }}>
                    <OverallRating style={{ justifyContent: 'center', margin: 0 }}>
                        <BigRating>{averageRating > 0 ? averageRating.toFixed(1) : '-'}</BigRating>
                        <RatingText>
                            <StarRating rating={averageRating} />
                            <RatingCount>{totalReviews} review{totalReviews !== 1 ? 's' : ''}</RatingCount>
                        </RatingText>
                    </OverallRating>
                </StatCard>
                
                <StatCard>
                    <div className="stat-title">5-Star Reviews</div>
                    <div className="stat-value" style={{ color: '#fbbf24', fontSize: 18 }}>
                        {fiveStarCount}
                    </div>
                </StatCard>
                
                <StatCard>
                    <div className="stat-title">Replied To</div>
                    <div className="stat-value" style={{ color: COLORS.success, fontSize: 18 }}>
                        {replyRate}%
                    </div>
                </StatCard>
            </StatGrid>
            
            {/* Section label */}
            <div style={{ 
                color: COLORS.muted, 
                fontSize: 10, 
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6
            }}>
                <FaComment style={{ fontSize: 9 }} /> Recent Reviews
            </div>
            
            {/* Reviews list */}
            {reviews.length === 0 ? (
                <EmptyState>
                    <FaComment />
                    <p>No reviews yet</p>
                    <p style={{ fontSize: 10, marginTop: 4, opacity: 0.7 }}>
                        Reviews will appear here after clients submit feedback
                    </p>
                </EmptyState>
            ) : (
                <ReviewsContainer>
                    {reviews.map((review, index) => {
                        const cIndex = colorIndexFromId(review.id || String(index));
                        const cleanService = sanitizeText(review.service) || 'Consultation';
                        return (
                            <ReviewCard key={review.id} $colorIndex={cIndex} onClick={() => setSelectedReview(review)}>
                                <ReviewHeader>
                                    <ReviewerInfo>
                                        <Avatar $colorIndex={cIndex}>{review.initials}</Avatar>
                                        <ReviewerDetails>
                                            <ReviewerName>{review.name}</ReviewerName>
                                            <ReviewDate>
                                                <FaCalendarAlt style={{ fontSize: 8 }} />
                                                {review.dateRelative}
                                            </ReviewDate>
                                        </ReviewerDetails>
                                    </ReviewerInfo>
                                    <StarRating rating={review.rating} />
                                </ReviewHeader>
                                
                                <ReviewText>{sanitizeText(review.shortText)}</ReviewText>
                                
                                <ReviewFooter>
                                    <ServiceType>{cleanService}</ServiceType>
                                    <ReadMore>
                                        Read more <FaChevronRight />
                                    </ReadMore>
                                </ReviewFooter>
                            </ReviewCard>
                        );
                    })}
                </ReviewsContainer>
            )}
            
            {/* Modal for full review - rendered via Portal to escape overflow:hidden */}
            {selectedReview && ReactDOM.createPortal(
                <ModalOverlay onClick={() => setSelectedReview(null)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={() => setSelectedReview(null)}>
                            <FaTimes />
                        </CloseButton>
                        
                        <ModalTitle>
                            <FaQuoteLeft /> Review Details
                        </ModalTitle>
                        
                        <ReviewerInfo style={{ marginBottom: 16 }}>
                            <Avatar 
                                $colorIndex={colorIndexFromId(selectedReview.id)} 
                                style={{ width: 48, height: 48, fontSize: 16 }}
                            >
                                {selectedReview.initials}
                            </Avatar>
                            <ReviewerDetails>
                                <ReviewerName style={{ fontSize: 16 }}>{selectedReview.name}</ReviewerName>
                                <ReviewDate style={{ fontSize: 12 }}>
                                    <FaCalendarAlt />
                                    {selectedReview.dateRelative}
                                </ReviewDate>
                            </ReviewerDetails>
                        </ReviewerInfo>
                        
                        <RatingBadge>
                            <FaStar /> {selectedReview.rating.toFixed(1)} / 5
                        </RatingBadge>
                        
                        <ServiceType style={{ fontSize: 11, padding: '4px 12px' }}>
                            {sanitizeText(selectedReview.service) || 'Consultation'}
                        </ServiceType>
                        
                        <FullReviewText>
                            <FaQuoteLeft style={{ fontSize: 20, display: 'block' }} />
                            {sanitizeText(selectedReview.fullText)}
                        </FullReviewText>
                        
                        {/* Detailed Ratings Breakdown */}
                        {selectedReview.ratings && (
                            <div style={{ 
                                background: 'rgba(0, 0, 0, 0.2)', 
                                borderRadius: 10, 
                                padding: 16,
                                marginTop: 16
                            }}>
                                <div style={{ 
                                    color: COLORS.light, 
                                    fontSize: 12, 
                                    fontWeight: 600, 
                                    marginBottom: 12 
                                }}>
                                    Rating Breakdown
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    {[
                                        { label: 'Professionalism', value: selectedReview.ratings.professionalism },
                                        { label: 'Actionable Advice', value: selectedReview.ratings.actionableAdvice },
                                        { label: 'Would Recommend', value: selectedReview.ratings.adviserRecommendation },
                                        { label: 'Navigation', value: selectedReview.ratings.navigation },
                                        { label: 'Instructions', value: selectedReview.ratings.instructions },
                                        { label: 'Process Flow', value: selectedReview.ratings.processFlow }
                                    ].map(item => (
                                        <div key={item.label} style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            fontSize: 11,
                                            color: COLORS.lightSecondary
                                        }}>
                                            <span>{item.label}</span>
                                            <span style={{ color: '#fbbf24', fontWeight: 600 }}>
                                                {item.value}/5
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Provenance, so an imported review is never read as one
                            collected here — and the published reply if there is one. */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            color: COLORS.muted,
                            fontSize: 12,
                            marginTop: 16,
                            flexWrap: 'wrap'
                        }}>
                            <FaThumbsUp style={{ color: COLORS.primary }} />
                            {selectedReview.service}
                            {selectedReview.translatedFrom && ` · translated from ${selectedReview.translatedFrom}`}
                            {selectedReview.textTruncated && ' · text shortened at the source'}
                        </div>

                        {selectedReview.adviserReply && (
                            <div style={{
                                marginTop: 12,
                                padding: 12,
                                borderRadius: 10,
                                background: 'rgba(0, 0, 0, 0.2)',
                                borderLeft: `2px solid ${COLORS.primary}`,
                                fontSize: 12,
                                lineHeight: 1.5,
                                color: COLORS.lightSecondary
                            }}>
                                <div style={{ fontWeight: 600, marginBottom: 4, color: COLORS.light }}>
                                    Your reply
                                </div>
                                {selectedReview.adviserReply}
                            </div>
                        )}
                    </ModalContent>
                </ModalOverlay>,
                document.body
            )}
        </PanelContent>
    );
};

export default ExpertReviews;
